import React, {
  Reducer,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import translations from './addpracticeplaceagreement.i18n';
import { NativeButton, ButtonVariant } from '../../components/button';
import PageLayout from '../../pagelayout';
import PanelHeader, {
  CustomStatus,
} from '../../components/panelheader/panelHeader';
import AgreementContent from './agreementcontent';
import * as css from './practiceplaceagreement.module.scss';
import {
  AvtalestatusDto,
  AvtalestederMedMuligeOmfattedePraksisstedPostDto,
  BrukerinfoDto as UHBrukerinfoDto,
  HalPraksisavtaleVedleggMinimalListDto as UHHalPraksisavtaleVedleggMinimalListDto,
  HalPraksisstedavtaleDto as UHHalPraksisstedavtaleDto,
  MinimalPraksisstedMedHierarkiDto,
  MinimalStudiestedDto,
  MultiSignatarerDto,
  PersonDto,
  PraksisavtaleValideringsfeilDto,
  PraksisavtaleVedleggTypeDto,
  PraksisaktivitetInfoDto,
  PraksisstedavtaleMedStedForelderHierarkiDto,
  PraksisstedavtalePostDto,
  PraksisstedavtaleViewDto,
  PraksisstedbrukerSignatarDto,
  PraksistypeDto,
  StudiestedbrukerSignatarDto,
  PraksisstedavtaleDto,
} from '../../api/generated/studiested-generated-api/index';
import {
  BrukerinfoDto as PSBrukerinfoDto,
  HalPraksisavtaleVedleggMinimalListDto as PSHalPraksisavtaleVedleggMinimalListDto,
  HalPraksisstedavtaleDto as PSHalPraksisstedavtaleDto,
} from '../../api/generated/praksissted-generated-api/index';
import StedSection, { Stedtype } from './stedsections';
import {
  GenericActionWithoutPayload,
  GenericActionWithPayload,
} from '../../util';
import PracticeTypes from './practicetypes';
import * as commonCss from './avtalesectionstyles.module.scss';
import AgreementDetails from './agreementdetails';
import useContextNotUndefined from '../../hooks/useContextNotUndefined';
import { UhaUserContext } from '../../user/UhaUserStore';
import { StudiestedApiContext } from '../../api/StudiestedApiContext';
import { NotificationFunctionContext } from '../../components/notifications/notificationProvider';
import { useLoader } from '../../api/useLoader';
import AgreementPermits from './agreementpermits';
import { mapNotUndefinedOrNull } from '../../util/listutils';
import {
  addNewVedlegg,
  Avtalevedlegg,
  removeVedleggByIdFromState,
  mapToAvtaleVedlegg,
  decorateVedleggWithExtraInfoFromBackend,
  usePostVedleggUpdates,
  Vedlegg,
} from './vedleggutils';
import { getDatoInstance, praksistyperToKoder } from './agreementutils';
import useAddErrorNotificationCallback from '../../hooks/useAddErrorNotificationCallback';
import { RoletypeDto } from '../../api/generated/login-generated-api';
import { SelectedRoleContext } from '../../providers';
import { devEnvFlag, testEnvFlag } from '../../util/featureFlags';
import { useStudiestedOrPraksisstedApi } from '../../api/useApi';
import { AgreementAktiviteterWarningModal } from './agreementactivitieswarning';
import useChangesCouldAffectAktiviteter from './useChangesCouldAffectAktiviteter';
import useUnsavedChangesPrompt from '../../hooks/useUnsavedChangesPrompt';
import AgreementDuplicateWarningSection, {
  AgreementDuplicateWarningConfirmation,
  useOverlapRequest,
} from './agreementduplicatewarning';
import { useNavigate, useParams } from 'react-router-dom';
import Spinner from '../../components/spinner/spinner';
import Colors from '../../alpColors';

// COMBINED TYPES

type Brukerinfo = UHBrukerinfoDto | PSBrukerinfoDto;

type HalPraksisavtaleVedleggMinimalList =
  | UHHalPraksisavtaleVedleggMinimalListDto
  | PSHalPraksisavtaleVedleggMinimalListDto;

type HalPraksisstedavtale =
  | UHHalPraksisstedavtaleDto
  | PSHalPraksisstedavtaleDto;

type AgreementHasChangedBySection = {
  avtalenavnHasChanged: boolean;
  startdatoHasChanged: boolean;
  sluttdatoHasChanged: boolean;
  studiestedHasChanged: boolean;
  praksisstedHasChanged: boolean;
  praksistypeHasChanged: boolean;
  vedleggHasChanged: boolean;
  alreadySignedHasChanged: boolean;
  sharePersonnrHasChanged: boolean;
};

/*
 * TODO Add state fields, as we go along
 * TODO Can be put/extracted directly into new contract
 */
export type AgreementState = {
  studiested: StudiestedForAvtale | undefined;
  praksissteder: PraksisstedForAvtale[] | undefined;
  praksistyper: PraksistypeDto[] | undefined;
  vedlegg: Avtalevedlegg;
  alreadySigned: boolean;
  delIdentitetsnummer: boolean;
  navn: string | undefined;
  startdato: string | undefined;
  sluttdato: string | undefined;
  ansvarlig: PersonDto | string | undefined;
  avtaleId: string | undefined;
  avtalestatus: AvtalestatusDto;
  valideringsfeil: PraksisavtaleValideringsfeilDto[];
  initVedlegg: Avtalevedlegg; // Needed separately, as vedlegg is not part of HalPraksisstedavtaleDto
  agreementHasUnsavedChanges: AgreementHasChangedBySection;
};

type AgreementStateWithInitial = AgreementState & {
  initAgreement: AgreementState | undefined;
};

export enum AgreementStateActionType {
  NEW_VEDLEGG,
  SET_ALREADY_SIGNED,
  SET_SHARE_PERSONNUMMER,
  SET_STUDIESTED,
  SET_PRAKSISSTEDER,
  SET_PRACTICE_TYPES,
  SET_AVTALENAVN,
  SET_STARTDATO,
  SET_SLUTTDATO,
  SET_VALIDERINGSFEIL,
  DELETE_VEDLEGG,
  DELETE_ALL_VEDLEGG,
  DECORATE_VEDLEGG_WITH_EXTRA_INFO_FROM_BACKEND,
  RESET_STATE_EXCEPT_VEDLEGG,
  SET_INIT_STATE,
  SET_VEDLEGG_AND_INIT_VEDLEGG,
}

/**
 * T is the main payload type, K is a key of AgreementHasChangedBySection
 * */
type AgreementPayloadWithChangeStatus<
  T,
  K extends keyof AgreementHasChangedBySection,
> = { content: T } & Pick<
  {
    [key in keyof AgreementHasChangedBySection]: boolean;
  },
  K
>;

export type AgreementStateAction =
  | GenericActionWithPayload<
      AgreementStateActionType.NEW_VEDLEGG,
      Vedlegg<PraksisavtaleVedleggTypeDto>
    >
  | GenericActionWithPayload<
      AgreementStateActionType.SET_ALREADY_SIGNED,
      boolean
    >
  | GenericActionWithPayload<
      AgreementStateActionType.SET_SHARE_PERSONNUMMER,
      boolean
    >
  | GenericActionWithPayload<
      AgreementStateActionType.SET_STUDIESTED,
      AgreementPayloadWithChangeStatus<
        StudiestedForAvtale | undefined,
        'studiestedHasChanged'
      >
    >
  | GenericActionWithPayload<
      AgreementStateActionType.SET_PRAKSISSTEDER,
      AgreementPayloadWithChangeStatus<
        PraksisstedForAvtale[] | undefined,
        'praksisstedHasChanged'
      >
    >
  | GenericActionWithPayload<
      AgreementStateActionType.SET_PRACTICE_TYPES,
      AgreementPayloadWithChangeStatus<
        PraksistypeDto[] | undefined,
        'praksistypeHasChanged'
      >
    >
  | GenericActionWithPayload<
      AgreementStateActionType.SET_AVTALENAVN,
      AgreementPayloadWithChangeStatus<
        string | undefined,
        'avtalenavnHasChanged'
      >
    >
  | GenericActionWithPayload<
      AgreementStateActionType.SET_STARTDATO,
      AgreementPayloadWithChangeStatus<string, 'startdatoHasChanged'>
    >
  | GenericActionWithPayload<
      AgreementStateActionType.SET_SLUTTDATO,
      AgreementPayloadWithChangeStatus<string, 'sluttdatoHasChanged'>
    >
  | GenericActionWithPayload<
      AgreementStateActionType.SET_VALIDERINGSFEIL,
      PraksisavtaleValideringsfeilDto[]
    >
  | GenericActionWithPayload<
      AgreementStateActionType.DELETE_VEDLEGG,
      AgreementPayloadWithChangeStatus<string, 'vedleggHasChanged'>
    >
  | GenericActionWithoutPayload<AgreementStateActionType.DELETE_ALL_VEDLEGG>
  | GenericActionWithPayload<
      AgreementStateActionType.DECORATE_VEDLEGG_WITH_EXTRA_INFO_FROM_BACKEND,
      Vedlegg<PraksisavtaleVedleggTypeDto>
    >
  | GenericActionWithPayload<
      AgreementStateActionType.RESET_STATE_EXCEPT_VEDLEGG,
      AgreementStateWithInitial
    >
  | GenericActionWithPayload<
      AgreementStateActionType.SET_INIT_STATE,
      AgreementState
    >
  | GenericActionWithPayload<
      AgreementStateActionType.SET_VEDLEGG_AND_INIT_VEDLEGG,
      Avtalevedlegg
    >;

const agreementReducer: Reducer<
  AgreementStateWithInitial,
  AgreementStateAction
> = (state, action) => {
  const { agreementHasUnsavedChanges } = state;

  switch (action.type) {
    case AgreementStateActionType.SET_ALREADY_SIGNED:
      return {
        ...state,
        alreadySigned: action.payload,
        agreementHasUnsavedChanges: {
          ...agreementHasUnsavedChanges,
          alreadySignedHasChanged:
            action.payload !== state.initAgreement?.alreadySigned,
        },
      };
    case AgreementStateActionType.SET_SHARE_PERSONNUMMER:
      return {
        ...state,
        delIdentitetsnummer: action.payload,
        agreementHasUnsavedChanges: {
          ...agreementHasUnsavedChanges,
          sharePersonnrHasChanged:
            action.payload !== state.initAgreement?.delIdentitetsnummer,
        },
      };
    case AgreementStateActionType.SET_STUDIESTED:
      return {
        ...state,
        studiested: action.payload.content,
        agreementHasUnsavedChanges: {
          ...agreementHasUnsavedChanges,
          studiestedHasChanged: action.payload.studiestedHasChanged,
        },
      };
    case AgreementStateActionType.SET_PRAKSISSTEDER:
      return {
        ...state,
        praksissteder: action.payload.content,
        agreementHasUnsavedChanges: {
          ...agreementHasUnsavedChanges,
          praksisstedHasChanged: action.payload.praksisstedHasChanged,
        },
      };
    case AgreementStateActionType.SET_PRACTICE_TYPES:
      return {
        ...state,
        praksistyper: action.payload.content,
        agreementHasUnsavedChanges: {
          ...agreementHasUnsavedChanges,
          praksistypeHasChanged: action.payload.praksistypeHasChanged,
        },
      };
    case AgreementStateActionType.SET_AVTALENAVN:
      return {
        ...state,
        navn: action.payload.content,
        agreementHasUnsavedChanges: {
          ...agreementHasUnsavedChanges,
          avtalenavnHasChanged: action.payload.avtalenavnHasChanged,
        },
      };
    case AgreementStateActionType.SET_STARTDATO:
      return {
        ...state,
        startdato: action.payload.content,
        agreementHasUnsavedChanges: {
          ...agreementHasUnsavedChanges,
          startdatoHasChanged: action.payload.startdatoHasChanged,
        },
      };
    case AgreementStateActionType.SET_SLUTTDATO:
      return {
        ...state,
        sluttdato: action.payload.content,
        agreementHasUnsavedChanges: {
          ...agreementHasUnsavedChanges,
          sluttdatoHasChanged: action.payload.sluttdatoHasChanged,
        },
      };
    case AgreementStateActionType.SET_VALIDERINGSFEIL:
      return {
        ...state,
        valideringsfeil: action.payload,
      };
    case AgreementStateActionType.NEW_VEDLEGG:
      return {
        ...state,
        vedlegg: addNewVedlegg(state.vedlegg, action.payload),
        agreementHasUnsavedChanges: {
          ...state.agreementHasUnsavedChanges,
          vedleggHasChanged: true,
        },
      };
    case AgreementStateActionType.DELETE_VEDLEGG:
      return {
        ...state,
        vedlegg: removeVedleggByIdFromState(
          state.vedlegg,
          action.payload.content,
        ),
        agreementHasUnsavedChanges: {
          ...state.agreementHasUnsavedChanges,
          vedleggHasChanged: action.payload.vedleggHasChanged,
        },
      };
    case AgreementStateActionType.DELETE_ALL_VEDLEGG:
      const initVedlegg = state.initAgreement?.vedlegg;

      return {
        ...state,
        vedlegg: {
          samarbeidsavtale: undefined,
          databehandleravtale: undefined,
          annet: [],
        },
        agreementHasUnsavedChanges: {
          ...state.agreementHasUnsavedChanges,
          vedleggHasChanged: !!(
            initVedlegg &&
            (initVedlegg.samarbeidsavtale ||
              initVedlegg.databehandleravtale ||
              initVedlegg.annet.length > 0)
          ),
        },
      };
    case AgreementStateActionType.DECORATE_VEDLEGG_WITH_EXTRA_INFO_FROM_BACKEND:
      return {
        ...state,
        vedlegg: decorateVedleggWithExtraInfoFromBackend(
          state.vedlegg,
          action.payload,
        ),
      };
    case AgreementStateActionType.RESET_STATE_EXCEPT_VEDLEGG:
      return { ...action.payload, vedlegg: state.vedlegg };
    case AgreementStateActionType.SET_INIT_STATE:
      return {
        ...action.payload,
        initAgreement: action.payload,
        agreementHasUnsavedChanges: initAgreementHasUnsavedChanges,
      };
    case AgreementStateActionType.SET_VEDLEGG_AND_INIT_VEDLEGG:
      return {
        ...state,
        vedlegg: action.payload,
        initVedlegg: action.payload,
        agreementHasUnsavedChanges: initAgreementHasUnsavedChanges,
      };
  }
  return state;
};

const initVedleggAgreementState: Avtalevedlegg = {
  samarbeidsavtale: undefined,
  databehandleravtale: undefined,
  annet: [],
};

const initAgreementHasUnsavedChanges: {
  [key in keyof AgreementHasChangedBySection]: false;
} = {
  avtalenavnHasChanged: false,
  startdatoHasChanged: false,
  sluttdatoHasChanged: false,
  studiestedHasChanged: false,
  praksisstedHasChanged: false,
  praksistypeHasChanged: false,
  vedleggHasChanged: false,
  alreadySignedHasChanged: false,
  sharePersonnrHasChanged: false,
};

// TODO Add fields and default values, as we go along
export const getInitNewAgreementState = (
  ansvarlig?: PersonDto,
): AgreementStateWithInitial => ({
  ansvarlig,
  navn: '',
  sluttdato: undefined,
  startdato: undefined,
  studiested: undefined,
  praksissteder: undefined,
  praksistyper: [],
  alreadySigned: false,
  delIdentitetsnummer: false,
  avtaleId: undefined,
  avtalestatus: AvtalestatusDto.Utkast,
  valideringsfeil: [],
  vedlegg: initVedleggAgreementState,
  initVedlegg: initVedleggAgreementState,
  initAgreement: undefined,
  agreementHasUnsavedChanges: initAgreementHasUnsavedChanges,
});

const getInitViewOrEditAgreementState = (
  avtale: HalPraksisstedavtale,
  vedlegg?: HalPraksisavtaleVedleggMinimalList,
): AgreementState => {
  const avtaleVedlegg = mapToAvtaleVedlegg(vedlegg?.items || []);

  return getUpdatedViewOrEditAgreementState(
    avtale,
    avtaleVedlegg,
    avtaleVedlegg,
  );
};

const getUpdatedViewOrEditAgreementState = (
  avtale: HalPraksisstedavtale,
  vedlegg: Avtalevedlegg,
  initVedlegg: Avtalevedlegg,
): AgreementStateWithInitial => {
  const {
    avtaleId,
    avtalenavn,
    avtalestart,
    avtaleslutt,
    signatarer,
    saksansvarlig,
    avtaleeier,
    avtalesteder,
    praksistyper,
    avtalestatus,
    avtaleErSignertAvAlle,
    delIdentitetsnummer,
    valideringsfeil,
  } = avtale as PraksisstedavtaleMedStedForelderHierarkiDto;

  const { omfattetStudiesteder, avtalestedforelder } = avtaleeier;

  const studiestedCheckedIds =
    omfattetStudiesteder?.map((sted) => sted.id) ?? [];

  const studiested: StudiestedForAvtale = {
    ...avtalestedforelder,
    checkedIds: studiestedCheckedIds,
    signatar: signatarer?.studiestedbrukere?.at(0), // TODO Currently only one signatar is allowed
  };

  const praksissteder: PraksisstedForAvtale[] = Object.entries(
    avtalesteder,
  ).map(([praksisstedId, avtalested]) => {
    const { avtalestedforelder: forelder, omfattetPraksisstedsteder } =
      avtalested;
    const { navn, orgnr, noder } = forelder;
    const checkedIds = (omfattetPraksisstedsteder || []).map((sted) => sted.id);

    return {
      id: praksisstedId,
      navn,
      orgnr,
      noder,
      checkedIds,
      signatar: signatarer?.praksisstedbrukere?.find(
        (bruker) => bruker.praksisstedId === praksisstedId,
      ),
    };
  });

  const agreementState: AgreementState = {
    ansvarlig: saksansvarlig,
    navn: avtalenavn,
    sluttdato: avtaleslutt,
    startdato: avtalestart,
    studiested,
    praksissteder,
    praksistyper,
    avtaleId,
    avtalestatus,
    valideringsfeil,
    vedlegg,
    initVedlegg,
    delIdentitetsnummer: !!delIdentitetsnummer,
    alreadySigned: !!avtaleErSignertAvAlle,
    agreementHasUnsavedChanges: initAgreementHasUnsavedChanges,
  };

  return {
    ...agreementState,
    initAgreement: agreementState,
  };
};

type AgreementContextProps = AgreementStateWithInitial & {
  canEditAgreement: boolean;
  isStudiestedbruker: boolean;
  dispatchAgreementEdit: React.Dispatch<AgreementStateAction>;
  validDraftInput: boolean;
};

export const AgreementContext = React.createContext<
  AgreementContextProps | undefined
>(undefined);

export const useAgreementContext = () =>
  useContextNotUndefined(AgreementContext, 'useAgreementContext');

enum SubmitButtonType {
  DRAFT,
  SEND,
}

const useAvtaleIdFromPath = (): string | undefined => {
  const { avtaleId } = useParams<{ avtaleId?: string }>();

  return avtaleId;
};

const useExistingAvtale = (): {
  avtaleForViewOrEditMode: HalPraksisstedavtale | undefined;
  avtaleLoading: boolean;
} => {
  const avtaleId = useAvtaleIdFromPath();
  const { praksisstedApi } = useStudiestedOrPraksisstedApi();

  const { data: avtaleForViewOrEditMode, loading: avtaleLoading } = useLoader<
    HalPraksisstedavtale | undefined
  >(
    () =>
      avtaleId
        ? praksisstedApi.avtalerPraksisIdGet({
            pathParams: { id: avtaleId },
            queryParams: {
              view: PraksisstedavtaleViewDto.PraksisstedavtaleMedStedForelderHierarki,
            },
          })
        : Promise.resolve(undefined),
    [avtaleId],
    false,
    true,
  );

  return { avtaleForViewOrEditMode, avtaleLoading };
};

const useVedlegg = (): {
  vedlegg: HalPraksisavtaleVedleggMinimalList | undefined;
  vedleggLoading: boolean;
} => {
  const { avtaleApi } = useStudiestedOrPraksisstedApi();
  const avtaleId = useAvtaleIdFromPath();

  const { data: vedlegg, loading: vedleggLoading } = useLoader(
    () =>
      avtaleId
        ? avtaleApi.avtalerPraksisAvtaleIdVedleggGet({
            pathParams: { avtaleId },
          })
        : Promise.resolve(undefined),
    [avtaleId],
  );

  return { vedlegg, vedleggLoading };
};

const useValidDraftInput = (agreement: AgreementState): boolean => {
  const startdatoInstance = useMemo(() => {
    return getDatoInstance(agreement.startdato);
  }, [agreement.startdato]);

  const sluttdatoInstance = useMemo(() => {
    return getDatoInstance(agreement.sluttdato);
  }, [agreement.sluttdato]);

  const validDato = useMemo(
    () =>
      !!startdatoInstance &&
      (!sluttdatoInstance || startdatoInstance < sluttdatoInstance),
    [startdatoInstance, sluttdatoInstance],
  );

  /*
   * TODO Validation
   * Checkboxes in AvtaleContent need validation
   * https://unit.atlassian.net/browse/ALP-2148 : Som studiestedbruker skal jeg kunne velge å
   * spesifisere at avtalen er allerede signert
   * https://unit.atlassian.net/browse/ALP-2151 : Som studiestedbruker skal jeg kunne velge om å
   * laste opp avtaleinnholdet på et senere tidspunkt
   */
  return useMemo<boolean>(
    () =>
      (agreement.praksistyper?.length || 0) > 0 &&
      !!agreement.studiested &&
      !!agreement.praksissteder &&
      !!agreement.navn &&
      validDato,
    [agreement, validDato],
  );
};

// TOP LEVEL PROVIDER UH USERS
export const PracticePlaceAgreementProviderUH = () => {
  const { uhaUser } = useContext(UhaUserContext);
  const brukerinfo = uhaUser!;

  return <PracticePlaceAgreementProvider brukerinfo={brukerinfo} />;
};

// TOP LEVEL PROVIDER PS USERS
export const PracticePlaceAgreementProviderPS = () => (
  <PracticePlaceAgreementProvider />
);

const PracticePlaceAgreementProvider: React.FC<
  React.PropsWithChildren<{
    brukerinfo?: Brukerinfo;
  }>
> = ({ brukerinfo }) => {
  const { type } = useContext(SelectedRoleContext)!;
  const [agreement, dispatchAgreementEdit] = useReducer(
    agreementReducer,
    getInitNewAgreementState(brukerinfo),
  );

  const validDraftInput = useValidDraftInput(agreement);

  const isStudiestedbruker = useMemo(
    () => type === RoletypeDto.Studiested,
    [type],
  );

  const { avtaleForViewOrEditMode, avtaleLoading } = useExistingAvtale();
  const { vedlegg: avtaleVedlegg, vedleggLoading } = useVedlegg();
  const canEditAgreement = useMemo<boolean>(
    () =>
      !avtaleLoading &&
      (!avtaleForViewOrEditMode || avtaleForViewOrEditMode.kanEndre),
    [avtaleLoading, avtaleForViewOrEditMode],
  );

  const isLoading = useMemo(
    () => avtaleLoading || vedleggLoading,
    [avtaleLoading, vedleggLoading],
  );

  const agreementContextValue = useMemo<AgreementContextProps>(
    () => ({
      ...agreement,
      dispatchAgreementEdit,
      validDraftInput,
      canEditAgreement,
      isStudiestedbruker,
    }),
    [
      agreement,
      dispatchAgreementEdit,
      validDraftInput,
      canEditAgreement,
      isStudiestedbruker,
    ],
  );

  useEffect(() => {
    if (!isLoading && avtaleForViewOrEditMode) {
      dispatchAgreementEdit({
        type: AgreementStateActionType.SET_INIT_STATE,
        payload: getInitViewOrEditAgreementState(
          avtaleForViewOrEditMode,
          avtaleVedlegg,
        ),
      });
    }
  }, [
    isLoading,
    avtaleForViewOrEditMode,
    avtaleVedlegg,
    dispatchAgreementEdit,
  ]);

  return (
    <AgreementContext.Provider value={agreementContextValue}>
      <PracticePlaceAgreementLayoutWrapper isLoading={isLoading} />
    </AgreementContext.Provider>
  );
};

const useAgreementHasUnsavedChanges = (): boolean => {
  const { agreementHasUnsavedChanges } = useAgreementContext();

  return Object.values(agreementHasUnsavedChanges).includes(true);
};

interface PracticePlaceAgreementLayoutWrapperProps {
  isLoading: boolean;
}

const PracticePlaceAgreementLayoutWrapper: React.FC<
  PracticePlaceAgreementLayoutWrapperProps
> = ({ isLoading }) => {
  const { formatMessage } = useIntl();
  const {
    dispatchAgreementEdit,
    validDraftInput,
    canEditAgreement,
    initAgreement,
    ...agreement
  } = useAgreementContext();
  const navigate = useNavigate();
  const avtaleIdFromPath = useAvtaleIdFromPath();

  const { praksisstedApi } = useContext(StudiestedApiContext);
  const { addSuccessNotification, addErrorNotification } = useContext(
    NotificationFunctionContext,
  );

  const labels = useMemo(() => {
    const isViewOrEditMode = !!initAgreement;

    return {
      title: formatMessage(
        {
          id: isViewOrEditMode
            ? translations.titleViewEdit.id
            : translations.title.id,
        },
        { avtalenavn: initAgreement?.navn || '' },
      ),
      subtitle: formatMessage({
        id: isViewOrEditMode
          ? translations.subtitleViewEdit.id
          : translations.subtitle.id,
      }),
      missingRequiredFields: formatMessage({
        id: translations.missingRequiredFields.id,
      }),
      agreementPostSuccess: formatMessage({
        id: translations.agreementPostSuccess.id,
      }),
    };
  }, [formatMessage, initAgreement]);

  const [isPosting, setIsPosting] = useState(false);
  const [updatedAvtaleId, setUpdatedAvtaleId] = useState<string>();
  const vedleggRequest = usePostVedleggUpdates();
  const errorCallback = useAddErrorNotificationCallback();

  useEffect(() => {
    if (!avtaleIdFromPath && updatedAvtaleId && !isPosting) {
      // Must redirect to correct URL (with avtaleId) on POST-request
      navigate(`/practiceplaces/practiceplaceagreement/${updatedAvtaleId}`);
    }
  }, [updatedAvtaleId, avtaleIdFromPath, isPosting, navigate]);

  const changesCouldAffectAktiviteter =
    useChangesCouldAffectAktiviteter(initAgreement);
  const [affectedAktiviteter, setAffectedAktiviteter] = useState<
    PraksisaktivitetInfoDto[]
  >([]);

  const [overlappingAgreements, setOverlappingAgreements] = useState<
    PraksisstedavtaleDto[]
  >([]);

  const sendAvtaleUtkast = () => {
    if (validDraftInput) {
      setIsPosting(true);

      // Vars tagged with Defined should be controlled by validInput
      const {
        studiested: studiestedDefined,
        praksissteder: praksisstederDefined,
        startdato: startdatoDefined,
        navn: avtalenavnDefined,
        praksistyper,
        ansvarlig,
        sluttdato,
        alreadySigned,
        delIdentitetsnummer,
        avtaleId,
        vedlegg,
        initVedlegg,
      } = agreement;

      const studiested = studiestedDefined!;
      const praksissteder = praksisstederDefined!;
      const avtalestart = startdatoDefined!;
      const avtalenavn = avtalenavnDefined!;
      const avtaleslutt = sluttdato || undefined;
      const praksistypekoder = praksistyperToKoder(praksistyper);
      const avtalesteder =
        mapPraksisstederForAvtaleToAvtalesteder(praksissteder);
      const signatarer = getSignatarerForNewAvtale([studiested], praksissteder);
      const saksansvarligPerson = ansvarlig as PersonDto;
      const saksansvarlig = saksansvarligPerson?.id
        ? {
            studiestedbrukerId: saksansvarligPerson?.id,
            navn: `${saksansvarligPerson.fornavn} ${saksansvarligPerson.etternavn}`,
          }
        : { navn: ansvarlig as string };

      const avtaleeier = {
        avtalestedforelderId: studiested.id,
        omfattetStudiestedIder: studiested.checkedIds,
      };

      const queryParams = {
        view: PraksisstedavtaleViewDto.PraksisstedavtaleMedStedForelderHierarki,
      };

      const praksisstedavtalePostDto: PraksisstedavtalePostDto = {
        avtalenavn,
        avtaleeier,
        avtalesteder,
        praksistypekoder,
        saksansvarlig,
        avtalestart,
        avtaleslutt,
        signatarer,
        avtaleErSignertAvAlle: alreadySigned,
        delIdentitetsnummer,
      };

      const postParams = {
        queryParams,
        praksisstedavtalePostDto,
      };
      const putParams = {
        pathParams: { id: avtaleId! },
        queryParams,
        praksisstedavtalePostDto,
      };

      const request = avtaleId
        ? praksisstedApi.avtalerPraksisIdPut(putParams)
        : praksisstedApi.avtalerPraksisPost(postParams);

      request
        .then((avtale) => {
          setUpdatedAvtaleId(avtale.avtaleId);

          dispatchAgreementEdit({
            type: AgreementStateActionType.RESET_STATE_EXCEPT_VEDLEGG,
            payload: getUpdatedViewOrEditAgreementState(
              avtale,
              vedlegg,
              initVedlegg,
            ),
          });

          return vedleggRequest(avtale.avtaleId, initVedlegg, vedlegg);
        })
        .then(() => addSuccessNotification(labels.agreementPostSuccess))
        .catch(errorCallback)
        .finally(() => setIsPosting(false));
    } else {
      addErrorNotification(labels.missingRequiredFields);
    }
  };

  const onCloseAgreementAktiviteterWarningModal = (confirmed: boolean) => {
    setAffectedAktiviteter([]);
    if (confirmed) sendAvtaleSecondConfirmation();
  };

  const onCloseAgreementDuplicateWarningConfirmation = (confirm: boolean) => {
    setOverlappingAgreements([]);
    if (confirm) sendAvtaleUtkast();
  };

  const onClickSendAvtale = () => {
    if (validDraftInput) {
      sendAvtaleFirstConfirmation();
    } else {
      addErrorNotification(labels.missingRequiredFields);
    }
  };

  const sendAvtaleFirstConfirmation = () => {
    if (initAgreement && changesCouldAffectAktiviteter) {
      const { avtaleId } = agreement;

      praksisstedApi
        .avtalerPraksisIdPraksisaktiviteterGet({
          pathParams: { id: avtaleId! },
        })
        .then(({ aktiviteter }) => {
          if (!aktiviteter?.length) {
            sendAvtaleSecondConfirmation();
          } else {
            setAffectedAktiviteter(aktiviteter);
          }
        });
    } else {
      sendAvtaleSecondConfirmation();
    }
  };

  const overlapRequest = useOverlapRequest();
  const sendAvtaleSecondConfirmation = () => {
    const {
      avtaleId,
      sluttdato,
      startdato,
      studiested,
      praksistyper,
      praksissteder,
    } = agreement;

    if (!startdato || !studiested || !praksistyper?.length || !praksissteder)
      return;

    overlapRequest({
      avtaleId,
      sluttdato,
      startdato,
      avtalestedIder: praksissteder.map((praksissted) => praksissted.id),
      praksistypekoder: praksistyper.map((praksistype) => praksistype.kode),
      studiestedIdEierAvAvtale: studiested.id,
    }).then((overlappendeAvtaler) => {
      if (overlappendeAvtaler.length === 0) {
        sendAvtaleUtkast();
      }
      setOverlappingAgreements(overlappendeAvtaler);
    });
  };

  const agreementHasUnsavedChanges = useAgreementHasUnsavedChanges();
  useUnsavedChangesPrompt(agreementHasUnsavedChanges);

  return (
    <PageLayout
      title={labels.title}
      subtitle={labels.subtitle}
      actionItems={
        <>
          <CancelBtn />
          {canEditAgreement && (
            <>
              <SubmitButton
                validInput={validDraftInput}
                hasUnsavedChanges={agreementHasUnsavedChanges}
                isPosting={isPosting}
                isLoading={isLoading}
                onClick={onClickSendAvtale}
                type={SubmitButtonType.DRAFT}
              />
              <SubmitButton
                // TODO The button should be deactivated until signering etc. is added
                validInput={false}
                hasUnsavedChanges={agreementHasUnsavedChanges}
                isPosting={isPosting}
                isLoading={isLoading}
                onClick={() => {
                  /* For show, for now */
                }}
                type={SubmitButtonType.SEND}
              />
            </>
          )}
        </>
      }
      isIndented
    >
      {isLoading ? <Spinner /> : <PracticePlaceAgreement />}
      {affectedAktiviteter.length > 0 && (
        <AgreementAktiviteterWarningModal
          onClose={onCloseAgreementAktiviteterWarningModal}
          aktiviteter={affectedAktiviteter}
        />
      )}
      {overlappingAgreements.length > 0 && (
        <AgreementDuplicateWarningConfirmation
          overlappendeAvtaler={overlappingAgreements}
          onClose={onCloseAgreementDuplicateWarningConfirmation}
        />
      )}
    </PageLayout>
  );
};

type SubmitButtonProps = {
  isLoading: boolean;
  isPosting: boolean;
  validInput: boolean;
  hasUnsavedChanges: boolean;
  onClick: () => void;
  type: SubmitButtonType;
};

const SubmitButton = React.memo<SubmitButtonProps>(
  ({ isLoading, isPosting, validInput, onClick, type, hasUnsavedChanges }) => {
    const { formatMessage } = useIntl();
    const labels = useMemo(
      () => ({
        sendAvtale: formatMessage({ id: translations.sendAvtale.id }),
        lagreUtkast: formatMessage({ id: translations.lagreUtkast.id }),
      }),
      [formatMessage],
    );

    const isPrimary = type === SubmitButtonType.SEND;

    return (
      <NativeButton
        /* TODO when sendAvtale is implemented
         * variant={isPrimary ? ButtonVariant.Primary : ButtonVariant.Tertiary}
         * */
        variant={ButtonVariant.Primary}
        data-cy={isPrimary ? 'submitAgreement' : 'submitAgreementDraft'}
        disabled={!(validInput && hasUnsavedChanges) || isPosting || isLoading}
        onClick={onClick}
      >
        {`${isPrimary ? labels.sendAvtale : labels.lagreUtkast}${
          isPosting ? '...' : ''
        }`}
      </NativeButton>
    );
  },
);

const CancelBtn = () => {
  const { isStudiestedbruker } = useAgreementContext();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const cancel = useMemo(
    () =>
      isStudiestedbruker
        ? formatMessage({ id: translations.cancel.id })
        : formatMessage({ id: translations.cancelView.id }),
    [formatMessage, isStudiestedbruker],
  );

  return (
    <NativeButton
      variant={ButtonVariant.Tertiary}
      onClick={() =>
        navigate(
          isStudiestedbruker
            ? '/practiceplaces/agreementdrafts'
            : '/myorganization/agreements',
        )
      }
      role={'link'}
    >
      {cancel}
    </NativeButton>
  );
};

type AvtaleStatusToTranslation = {
  [status in AvtalestatusDto]: string;
};

const getClassNameForAvtalestatus = (avtalestatus: AvtalestatusDto) => {
  switch (avtalestatus) {
    // TODO Implement different color schemes, when necessary
    case AvtalestatusDto.Aktiv:
    case AvtalestatusDto.Avsluttet:
    case AvtalestatusDto.IkkeAktiv:
    case AvtalestatusDto.InnhenterOpplysninger:
    case AvtalestatusDto.Kansellert:
    case AvtalestatusDto.SendtTilSignering:
    case AvtalestatusDto.Utkast:
      return css.utkastColor;
    case AvtalestatusDto.Feil:
      return css.feilColor;
  }
};

const getAvtalestatusForHeader = (
  avtalestatus: AvtalestatusDto,
  statusTranslations: AvtaleStatusToTranslation,
): CustomStatus => ({
  statusTitle: statusTranslations[avtalestatus],
  className: getClassNameForAvtalestatus(avtalestatus),
});

const PracticePlaceAgreement = () => {
  const { formatMessage } = useIntl();
  const { avtalestatus, canEditAgreement, initAgreement } =
    useAgreementContext();

  const labels = useMemo(
    () => ({
      avtaletitle: formatMessage({ id: translations.avtaletitle.id }),
      avtalesubtitle: formatMessage({
        id: canEditAgreement
          ? translations.avtalesubtitle.id
          : translations.avtalesubtitleView.id,
      }),
      studiested: formatMessage({ id: translations.studiested.id }),
      studiestedSubtitle: formatMessage({
        id: canEditAgreement
          ? translations.studiestedSubtitle.id
          : translations.studiestedSubtitleView.id,
      }),
      praksissteder: formatMessage({ id: translations.praksissteder.id }),
      praksisstederSubtitle: formatMessage({
        id: canEditAgreement
          ? translations.praksisstederSubtitle.id
          : translations.praksisstederSubtitleView.id,
      }),
      praksistype: formatMessage({ id: translations.praksistype.id }),
      praksistypeSubtitle: formatMessage({
        id: canEditAgreement
          ? translations.praksistypeSubtitle.id
          : translations.praksistypeSubtitleView.id,
      }),
      avtaleinnhold: formatMessage({ id: translations.avtaleinnhold.id }),
      avtaleinnholdSubtitle: formatMessage({
        id: canEditAgreement
          ? translations.avtaleinnholdSubtitle.id
          : translations.avtaleinnholdSubtitleView.id,
      }),
      tillatelser: formatMessage({ id: translations.tillatelser.id }),
      signering: formatMessage({ id: translations.signering.id }),
      signeringSubtitle: formatMessage({
        id: translations.signeringSubtitle.id,
      }),
      statusIkkeAktiv: formatMessage({ id: translations.statusIkkeAktiv.id }),
      statusAktiv: formatMessage({ id: translations.statusAktiv.id }),
      statusSendtTilSignering: formatMessage({
        id: translations.statusSendtTilSignering.id,
      }),
      statusInnhenterOpplysninger: formatMessage({
        id: translations.statusInnhenterOpplysninger.id,
      }),
      statusKansellert: formatMessage({ id: translations.statusKansellert.id }),
      statusAvsluttet: formatMessage({ id: translations.statusAvsluttet.id }),
      statusUtkast: formatMessage({ id: translations.statusUtkast.id }),
      statusFeil: formatMessage({ id: translations.statusFeil.id }),
    }),
    [formatMessage, canEditAgreement],
  );

  const customStatus = useMemo(() => {
    const avtalestatusTranslations = {
      [AvtalestatusDto.IkkeAktiv]: labels.statusIkkeAktiv,
      [AvtalestatusDto.Aktiv]: labels.statusAktiv,
      [AvtalestatusDto.SendtTilSignering]: labels.statusSendtTilSignering,
      [AvtalestatusDto.InnhenterOpplysninger]:
        labels.statusInnhenterOpplysninger,
      [AvtalestatusDto.Kansellert]: labels.statusKansellert,
      [AvtalestatusDto.Avsluttet]: labels.statusAvsluttet,
      [AvtalestatusDto.Utkast]: labels.statusUtkast,
      [AvtalestatusDto.Feil]: labels.statusFeil,
    };

    return getAvtalestatusForHeader(avtalestatus, avtalestatusTranslations);
  }, [avtalestatus, labels]);

  return (
    <div className={css.agreementContent}>
      {canEditAgreement && <ValideringsfeilAvtale />}
      <PanelHeader
        title={labels.avtaletitle}
        subtitle={labels.avtalesubtitle}
        childrenClassName={css.headerChildren}
        customStatus={customStatus}
        collapsible
      >
        <AgreementDetails />
      </PanelHeader>
      <PanelHeader
        title={labels.studiested}
        subtitle={labels.studiestedSubtitle}
        childrenClassName={css.headerChildren}
        collapsible
      >
        <StedSection stedtype={Stedtype.STUDIESTED} />
      </PanelHeader>
      <PanelHeader
        title={labels.praksissteder}
        subtitle={labels.praksisstederSubtitle}
        childrenClassName={css.headerChildren}
        collapsible
      >
        <StedSection stedtype={Stedtype.PRAKSISSTED} />
      </PanelHeader>
      <PanelHeader
        title={labels.praksistype}
        subtitle={labels.praksistypeSubtitle}
        childrenClassName={css.headerChildren}
        collapsible
      >
        <PracticeTypes />
      </PanelHeader>
      <PanelHeader
        title={labels.avtaleinnhold}
        subtitle={labels.avtaleinnholdSubtitle}
        childrenClassName={css.headerChildren}
        collapsible
      >
        <AgreementContent />
      </PanelHeader>
      <PanelHeader
        title={labels.tillatelser}
        childrenClassName={css.headerChildren}
        collapsible
      >
        <AgreementPermits />
      </PanelHeader>
      {(devEnvFlag || testEnvFlag) && (
        <PanelHeader
          title={labels.signering}
          subtitle={labels.signeringSubtitle}
          childrenClassName={css.headerChildren}
          collapsible
        >
          {'(Innhold kommer)'}
        </PanelHeader>
      )}
      {canEditAgreement && initAgreement && (
        <AgreementDuplicateWarningSection initAgreement={initAgreement} />
      )}
    </div>
  );
};

const mapPraksisstederForAvtaleToPraksisstedSignatarer = (
  praksissteder: PraksisstedForAvtale[],
): PraksisstedbrukerSignatarDto[] =>
  mapNotUndefinedOrNull(praksissteder, (praksissted) => {
    const { signatar, id } = praksissted;

    if (!signatar) return;

    const { telefonnummer } = signatar;

    return {
      ...signatar,
      telefonnummer: telefonnummer?.replace(' ', ''),
      praksisstedId: id,
    };
  });

const getSignatarerForNewAvtale = (
  studiesteder: StudiestedForAvtale[],
  praksissteder: PraksisstedForAvtale[],
): MultiSignatarerDto => {
  const studiestedbrukere = mapNotUndefinedOrNull(
    studiesteder,
    (studiested) => studiested.signatar,
  );

  return {
    studiestedbrukere,
    praksisstedbrukere:
      mapPraksisstederForAvtaleToPraksisstedSignatarer(praksissteder),
  };
};

const mapPraksisstederForAvtaleToAvtalesteder = (
  praksissteder: PraksisstedForAvtale[],
): AvtalestederMedMuligeOmfattedePraksisstedPostDto[] =>
  praksissteder.map(({ id, checkedIds }) => ({
    avtalestedforelderId: id,
    omfattetPraksisstedstedIder: checkedIds,
  }));

const ValideringsfeilAvtale = React.memo(() => {
  const { valideringsfeil } = useAgreementContext();
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      fantValideringsfeilTitle: formatMessage({
        id: translations.fantValideringsfeilTitle.id,
      }),
    }),
    [formatMessage],
  );

  if (valideringsfeil.length === 0) return <></>;

  return (
    <PanelHeader
      title={labels.fantValideringsfeilTitle}
      childrenClassName={css.headerChildren}
      color={Colors.mojo}
      collapsible
    >
      <ul>
        {valideringsfeil.map((feiltype, index) => (
          <li key={`valideringsfeil-${index}`}>{feiltype.message}</li>
        ))}
      </ul>
    </PanelHeader>
  );
});

// Change type fields as needed - TODO Can be put/extracted directly into new contract
export type StudiestedForAvtale = MinimalStudiestedDto & {
  checkedIds: string[];
  signatar: StudiestedbrukerSignatarDto | undefined;
};

// Kept for later, when sendAvtale is implemented (v3)
export type NewPraksisstedSignatar = {
  fornavn: string;
  etternavn: string;
  telefonnummer: string;
  epost: string;
};

export type NewPraksisstedSignatarDraft = {
  fornavn?: string;
  etternavn?: string;
  telefonnummer?: string;
  epost?: string;
};

// Change type fields as needed - TODO Can be put/extracted directly into new contract
export type PraksisstedForAvtale = MinimalPraksisstedMedHierarkiDto & {
  checkedIds: string[];
  signatar: NewPraksisstedSignatar | NewPraksisstedSignatarDraft | undefined;
};

export const getCssBasedOnModalHeight = (dimensions: {
  documentHeight: number;
  modalHeight: number;
}): string => {
  const { documentHeight, modalHeight } = dimensions;

  return modalHeight > documentHeight / 1.5
    ? commonCss.overflowYAuto
    : commonCss.overflowYVisible;
};
